import type { DxfMappingRole } from '@frontend/api';
import {
  IconButtonGroup,
  IconButton,
  Tooltip,
  Text,
} from '@frontend/legacy-components';

import { brushHotkeyLabel } from 'modules/common/viewer/dxfViewer/session/brushHotkeys';
import {
  MAPPING_ROLE_DEFINITIONS,
  activeRoleLabelColor,
} from 'modules/common/viewer/dxfViewer/session/roles';
import { brushToolId } from 'modules/common/viewer/dxfViewer/session/toolController';
import type { ToolId } from 'modules/common/viewer/dxfViewer/session/types';

export type DxfBrushToolGroupProps = {
  /** The session's current tool, so the active member of the group is lit. */
  activeTool: ToolId;
  /** Arms a role's brush. Repeated calls for the same role are a no-op. */
  onArmBrush: (role: DxfMappingRole) => void;
  /** Switches to plain selection, disarming any brush. */
  onChoose: () => void;
  /** The whole group is inert while the file is locked. */
  isDisabled?: boolean;
  /** Shown when the group is disabled because the file is locked. */
  disabledReason?: string;
};

/**
 * The mapping toolbar's tool strip: Choose, plus one brush per configured
 * role — five mutually exclusive members of the same radio group `ToolId`
 * models, Choose being the plain `'select'` value.
 *
 * A different component from `DxfRoleActions`, deliberately: that one assigns
 * a role straight to the current selection (the flyout's job), while this one
 * arms a *tool* that paints on a later click or marquee — same role registry
 * and colours, but a different action and a fifth, non-role member.
 */
export const DxfBrushToolGroup = ({
  activeTool,
  onArmBrush,
  onChoose,
  isDisabled = false,
  disabledReason,
}: DxfBrushToolGroupProps) => (
  <Tooltip
    placement="bottom"
    label={disabledReason}
    isDisabled={!isDisabled || disabledReason === undefined}
    shouldWrapChildren={isDisabled}
  >
    <IconButtonGroup
      size="md"
      borderWidth="1px"
      borderColor="separator.divider"
      borderStyle="solid"
      borderRadius="md"
    >
      <Tooltip placement="bottom" label="Choose (Esc)">
        <IconButton
          w="auto"
          aria-label="Choose"
          aria-pressed={activeTool === 'select'}
          isDisabled={isDisabled}
          onClick={onChoose}
          px={1}
          m={0}
          borderRadius="3px"
          {...(activeTool === 'select' && {
            bg: 'interaction.active',
            color: 'feature.onInteraction',
            _hover: { bg: 'interaction.active' },
          })}
        >
          <Text color="inherit" fontWeight="heavy">
            Choose
          </Text>
        </IconButton>
      </Tooltip>
      {MAPPING_ROLE_DEFINITIONS.map(({ role, label, color }) => {
        const isActive = activeTool === brushToolId(role);

        return (
          <Tooltip
            key={role}
            placement="bottom"
            label={`${label} (${brushHotkeyLabel(role)})`}
          >
            <IconButton
              aria-label={`Arm ${label} brush`}
              aria-pressed={isActive}
              isDisabled={isDisabled}
              w="auto"
              onClick={() => onArmBrush(role)}
              px={1}
              m={0}
              borderRadius="3px"
              // The role's own color carries the meaning; the variant carries the
              // state. Tinted only when active, so the group reads as a set of
              // choices rather than four competing colours. Ignore's chip fill is
              // light, so it gets a strong foreground instead of the usual
              // white-on-color — see `activeRoleLabelColor`.
              {...(isActive && {
                bg: color,
                color: activeRoleLabelColor(role),
                _hover: { bg: color },
              })}
            >
              <Text color="inherit" fontWeight="heavy">
                {label}
              </Text>
            </IconButton>
          </Tooltip>
        );
      })}
    </IconButtonGroup>
  </Tooltip>
);
DxfBrushToolGroup.displayName = 'DxfBrushToolGroup';
